import { type ReactNode } from "react";
import { usePermissions } from "@/src/lib/usePermissions/usePermissions";
import { CircularProgress, Box } from "@mui/material";
import { type Permission } from "@/src/lib/usePermissions/Permission.type";
import { useTranslation } from "react-i18next";

interface ProtectedRouteProps {
    children: ReactNode;
    requiredPermission?: Permission;
    requiredPermissions?: Permission[];
    requireAll?: boolean;
}

export const ProtectedRoute = ({
    children,
    requiredPermission,
    requiredPermissions,
    requireAll = false,
}: ProtectedRouteProps) => {
    const { t } = useTranslation("common");
    const { hasPermission, hasAnyPermission, hasAllPermissions, loading } =
        usePermissions();

    if (loading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100vh"
            >
                <CircularProgress />
            </Box>
        );
    }

    const hasAccess = () => {
        if (requiredPermission) {
            return hasPermission(requiredPermission);
        }

        if (requiredPermissions) {
            return requireAll
                ? hasAllPermissions(requiredPermissions)
                : hasAnyPermission(requiredPermissions);
        }

        return true;
    };

    if (!hasAccess()) {
        return <div>{t("no-permissions")}</div>;
    }

    return <>{children}</>;
};
